import autoTable from 'jspdf-autotable';
import { DocumentConciliationRateHistory } from 'lib/common/GeneralComponents/DocumentProfile/components/types';
import { formatDate, formatMoney } from 'lib/functions/utils';

import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';
import { FieldVisibilityConfig } from './types';

interface DocumentReportDocument {
  businessIdentifier: string;
  stakeholderIdentifier: string;
  requestId: string;
  type: string;
  folio: string;
  status: string;
  businessName: string;
  stakeholderName: string;
  operationDate: Date;
  rate: number;
  amount: number;
  interestCost: number;
  paymentDate: Date;
  startingPaymentDate?: Date | string;
  advance: number;
  arrearRate?: number;
  amountNet: number;
  daysToCount: number;
  amountConciliated: number;
  arrearConciliation: number;
  documentConciliation: number;
  documentAmountDiscounted?: number;
  conciliationLog?: any[];
  arrearAmount: number;
  documentRemainingAmountNet: number;
  totalRemainingNet: number;
  creditNoteAmount: number;
  extensions?: Array<{
    id: number;
    startDate: Date | string;
    dueDate: Date | string;
    rate: number;
    status: string;
    statusEsp: string;
  }>;
}

interface DocumentReportConfig extends PDFGeneratorConfig {
  document: DocumentReportDocument;
  fieldVisibility?: FieldVisibilityConfig;
  rateHistory?: DocumentConciliationRateHistory;
}

export class DocumentReportGenerator extends BasePDFGenerator {
  private document: DocumentReportDocument;
  private fieldVisibility: FieldVisibilityConfig;
  private rateHistory?: DocumentConciliationRateHistory;

  constructor(config: DocumentReportConfig) {
    super(config);
    this.document = config.document;
    this.fieldVisibility = config.fieldVisibility || {};
    this.rateHistory = config.rateHistory;
  }

  private shouldShowField(fieldName: keyof FieldVisibilityConfig): boolean {
    return this.fieldVisibility[fieldName] !== false;
  }

  private drawKeyValueIfVisible(
    label: string,
    value: string,
    x: number,
    y: number,
    valueOffset: number,
    fieldName: keyof FieldVisibilityConfig,
  ): number {
    if (this.shouldShowField(fieldName)) {
      this.drawKeyValue(label, value, x, y, valueOffset);
      return y + 5; // Retorna la siguiente posición Y
    }
    return y; // Retorna la misma posición Y si no se muestra
  }

  protected addSummarySection(): void {
    const col1X = 10,
      col2X = 110,
      valueOffset = 35,
      initialY = 35;

    // Primera columna - Información básica
    let col1Y = initialY;
    col1Y = this.drawKeyValueIfVisible(
      'Rut cliente',
      this.document.businessIdentifier,
      col1X,
      col1Y,
      valueOffset,
      'businessIdentifier',
    );
    col1Y = this.drawKeyValueIfVisible(
      'Rut deudor',
      this.document.stakeholderIdentifier,
      col1X,
      col1Y,
      valueOffset,
      'stakeholderIdentifier',
    );
    col1Y = this.drawKeyValueIfVisible(
      'Nro. operación',
      this.document.requestId,
      col1X,
      col1Y,
      valueOffset,
      'requestId',
    );
    col1Y = this.drawKeyValueIfVisible(
      'Tipo documento',
      this.document.type,
      col1X,
      col1Y,
      valueOffset,
      'type',
    );

    // Segunda columna - Información básica
    let col2Y = initialY;
    col2Y = this.drawKeyValueIfVisible(
      'Nombre cliente',
      getShortString(this.document.businessName, 25),
      col2X,
      col2Y,
      valueOffset,
      'businessName',
    );
    col2Y = this.drawKeyValueIfVisible(
      'Nombre deudor',
      getShortString(this.document.stakeholderName, 25),
      col2X,
      col2Y,
      valueOffset,
      'stakeholderName',
    );
    col2Y = this.drawKeyValueIfVisible(
      'Folio',
      this.document.folio,
      col2X,
      col2Y,
      valueOffset,
      'folio',
    );
    col2Y = this.drawKeyValueIfVisible(
      'Estado',
      this.document.status,
      col2X,
      col2Y,
      valueOffset,
      'status',
    );

    // Calcular la posición Y para la sección de resumen basada en la altura máxima de las columnas
    const maxBasicInfoY = Math.max(col1Y, col2Y);
    const resumenY = maxBasicInfoY + 5;

    this.drawText('Resumen operación', col1X, resumenY, {
      size: 12,
      bold: true,
    });

    // Primera columna - Resumen operación
    let resumenCol1Y = resumenY + 10;
    resumenCol1Y = this.drawKeyValueIfVisible(
      'Fecha operación',
      formatDate(this.document.operationDate),
      col1X,
      resumenCol1Y,
      valueOffset,
      'operationDate',
    );
    resumenCol1Y = this.drawKeyValueIfVisible(
      'Tasa negocio',
      `${this.document.rate}%`,
      col1X,
      resumenCol1Y,
      valueOffset,
      'rate',
    );
    resumenCol1Y = this.drawKeyValueIfVisible(
      'Monto documento',
      formatMoney(this.document.amount),
      col1X,
      resumenCol1Y,
      valueOffset,
      'amount',
    );
    resumenCol1Y = this.drawKeyValueIfVisible(
      'Costo interés',
      formatMoney(this.document.interestCost),
      col1X,
      resumenCol1Y,
      valueOffset,
      'interestCost',
    );
    resumenCol1Y = this.drawKeyValueIfVisible(
      'Fecha vencimiento',
      formatDate(this.document.paymentDate),
      col1X,
      resumenCol1Y,
      valueOffset,
      'paymentDate',
    );

    // Segunda columna - Resumen operación
    let resumenCol2Y = resumenY + 10;
    resumenCol2Y = this.drawKeyValueIfVisible(
      'Anticipo',
      `${this.document.advance}%`,
      col2X,
      resumenCol2Y,
      valueOffset,
      'advance',
    );
    resumenCol2Y = this.drawKeyValueIfVisible(
      'Tasa mora',
      `${String(this.document.arrearRate)}%`,
      col2X,
      resumenCol2Y,
      valueOffset,
      'arrearRate',
    );
    resumenCol2Y = this.drawKeyValueIfVisible(
      'Monto anticipado',
      formatMoney(this.document.amountNet),
      col2X,
      resumenCol2Y,
      valueOffset,
      'amountNet',
    );
    resumenCol2Y = this.drawKeyValueIfVisible(
      'Monto retenido',
      formatMoney(this.document.amount - this.document.amountNet),
      col2X,
      resumenCol2Y,
      valueOffset,
      'amountNet', // Usa amountNet como referencia ya que monto retenido depende de amount y amountNet
    );
    resumenCol2Y = this.drawKeyValueIfVisible(
      'Días',
      String(this.document.daysToCount),
      col2X,
      resumenCol2Y,
      valueOffset,
      'daysToCount',
    );

    // Calcular la posición Y para la sección de recaudaciones
    const maxResumenY = Math.max(resumenCol1Y, resumenCol2Y);
    const recaudaY = maxResumenY + 10;

    this.drawText('Recaudaciones', col1X, recaudaY, { size: 12, bold: true });

    let recaudaCol1Y = recaudaY + 10;
    recaudaCol1Y = this.drawKeyValueIfVisible(
      'Abono Capital',
      formatMoney(this.document.documentConciliation),
      col1X,
      recaudaCol1Y,
      valueOffset,
      'documentConciliation',
    );
    recaudaCol1Y = this.drawKeyValueIfVisible(
      'Monto Condonado',
      formatMoney(this.document.documentAmountDiscounted || 0),
      col1X,
      recaudaCol1Y,
      valueOffset,
      'documentAmountDiscounted',
    );

    const recaudaCol2Y = recaudaY + 10;
    this.drawKeyValueIfVisible(
      'Mora calculada',
      formatMoney(this.document.arrearConciliation),
      col2X,
      recaudaCol2Y,
      valueOffset,
      'arrearConciliation',
    );
  }

  protected addContentSection(): void {
    // La tabla de recaudaciones debe ir después de la sección de recaudaciones
    // Calcular la posición Y después de las recaudaciones
    const recaudacionesEndY = this.getRecaudacionesEndY();

    // Determinar si debemos mostrar la columna de tasa aplicada
    const showRateColumn =
      this.shouldShowField('rateHistory') &&
      this.rateHistory?.conciliations &&
      this.rateHistory.conciliations.length > 0;

    // Construir el header de la tabla
    const tableHead = [
      'ID operación',
      'Tipo de operación',
      'Pagado por',
      'Fecha pago',
      'Monto',
      'Capital',
      'Mora',
    ];

    if (showRateColumn) {
      tableHead.push('Tasa aplicada');
    }

    // Construir el body de la tabla
    const tableBody = (this.document.conciliationLog || []).map((log: any) => {
      const row = [
        log.requestId,
        log.typeEsp,
        log.paidBy,
        formatDate(log.payInDate),
        formatMoney(log.amount),
        formatMoney(log.conciliationDocumentAmount),
        formatMoney(log.conciliationArrearAmount),
      ];

      if (showRateColumn) {
        const arrearRate = this.getArrearRateForConciliation(log);
        row.push(arrearRate || '-');
      }

      return row;
    });

    // Construir estilos de columna
    const columnStyles: Record<number, { cellWidth: number }> = {
      0: { cellWidth: 20 },
      1: { cellWidth: 25 },
    };

    // Ajustar anchos si hay columna de tasa
    if (showRateColumn) {
      // Reducir ligeramente otras columnas para hacer espacio
      columnStyles[0] = { cellWidth: 18 };
      columnStyles[1] = { cellWidth: 23 };
      columnStyles[7] = { cellWidth: 18 }; // Columna de tasa aplicada
    }

    // Primero dibujar la tabla de recaudaciones
    autoTable(this.doc, {
      startY: recaudacionesEndY + 10, // Agregar 10 puntos de espacio
      head: [tableHead],
      body: tableBody,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      columnStyles,
    });

    // Calcular la posición Y después de la tabla de recaudaciones
    const tableHeight = (this.document.conciliationLog?.length || 0) * 8 + 20; // Estimación de altura
    const estadoY = recaudacionesEndY + 10 + tableHeight + 10; // Posición después de la tabla

    // Luego dibujar la sección de estado actual
    this.drawText('Estado actual', 10, estadoY, { size: 12, bold: true });

    // Preparar el body de la tabla de estado actual basado en la visibilidad
    const estadoBody = [];

    if (this.shouldShowField('arrearAmount')) {
      estadoBody.push(['Saldo mora', formatMoney(this.document.arrearAmount)]);
    }
    if (this.shouldShowField('documentRemainingAmountNet')) {
      estadoBody.push([
        'Saldo capital',
        formatMoney(this.document.documentRemainingAmountNet),
      ]);
    }
    if (this.shouldShowField('totalRemainingNet')) {
      estadoBody.push([
        'Saldo total',
        formatMoney(this.document.totalRemainingNet),
      ]);
    }
    if (this.shouldShowField('creditNoteAmount')) {
      estadoBody.push([
        'Monto Nota de crédito',
        formatMoney(this.document.creditNoteAmount),
      ]);
    }

    if (estadoBody.length > 0) {
      autoTable(this.doc, {
        startY: estadoY + 5,
        head: [['Saldo', 'Monto']],
        body: estadoBody,
        theme: 'grid',
        headStyles: {
          fillColor: '#efefef',
          textColor: '#1b1b1b',
          fontStyle: 'bold',
          halign: 'left',
          valign: 'middle',
          lineColor: [44, 62, 80],
          fontSize: 9,
        },
        bodyStyles: {
          textColor: [50, 50, 50],
          fontSize: 8,
        },
        styles: {
          font: 'helvetica',
          lineColor: [44, 62, 80],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 50 },
        },
      });
    }

    // Agregar tabla de prórrogas si existen y está habilitado en la configuración
    if (this.shouldShowField('extensions')) {
      const extensions = this.document.extensions || [];
      if (extensions.length > 0) {
        // Obtener la posición final de la tabla de Estado actual
        const lastTable = (this.doc as any).lastAutoTable;
        const prorrogasTitleY = lastTable
          ? lastTable.finalY + 15
          : estadoY + 50;

        // Dibujar título de Prórrogas
        this.drawText('Prórrogas', 10, prorrogasTitleY, {
          size: 12,
          bold: true,
        });

        // Posición de inicio de la tabla de prórrogas (después del título)
        const prorrogasY = prorrogasTitleY + 5;

        // Ordenar extensiones por fecha de inicio
        const sortedExtensions = [...extensions].sort((a, b) => {
          const dateA = new Date(a.startDate).getTime();
          const dateB = new Date(b.startDate).getTime();
          return dateA - dateB;
        });

        const prorrogasBody = sortedExtensions.map((extension) => [
          formatDate(
            extension.startDate instanceof Date
              ? extension.startDate
              : new Date(extension.startDate),
          ),
          formatDate(
            extension.dueDate instanceof Date
              ? extension.dueDate
              : new Date(extension.dueDate),
          ),
          `${extension.rate}%`,
          extension.statusEsp || extension.status || 'N/A',
        ]);

        autoTable(this.doc, {
          startY: prorrogasY,
          head: [['Fecha inicio', 'Fecha vencimiento', 'Tasa', 'Estado']],
          body: prorrogasBody,
          theme: 'grid',
          headStyles: {
            fillColor: '#efefef',
            textColor: '#1b1b1b',
            fontStyle: 'bold',
            halign: 'left',
            valign: 'middle',
            lineColor: [44, 62, 80],
            fontSize: 9,
          },
          bodyStyles: {
            textColor: [50, 50, 50],
            fontSize: 8,
          },
          styles: {
            font: 'helvetica',
            lineColor: [44, 62, 80],
            lineWidth: 0.1,
          },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 40 },
            2: { cellWidth: 25 },
            3: { cellWidth: 35 },
          },
        });
      }
    }
  }

  private getRecaudacionesEndY(): number {
    const initialY = 35;

    // Primera columna - Información básica
    let col1Y = initialY;
    col1Y = this.calculateNextY(col1Y, 'businessIdentifier');
    col1Y = this.calculateNextY(col1Y, 'stakeholderIdentifier');
    col1Y = this.calculateNextY(col1Y, 'requestId');
    col1Y = this.calculateNextY(col1Y, 'type');

    // Segunda columna - Información básica
    let col2Y = initialY;
    col2Y = this.calculateNextY(col2Y, 'businessName');
    col2Y = this.calculateNextY(col2Y, 'stakeholderName');
    col2Y = this.calculateNextY(col2Y, 'folio');
    col2Y = this.calculateNextY(col2Y, 'status');

    // Calcular la posición Y para la sección de resumen basada en la altura máxima de las columnas
    const maxBasicInfoY = Math.max(col1Y, col2Y);
    const resumenY = maxBasicInfoY + 5;

    // Primera columna - Resumen operación
    let resumenCol1Y = resumenY + 10;
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'operationDate');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'rate');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'amount');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'interestCost');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'paymentDate');

    // Segunda columna - Resumen operación
    let resumenCol2Y = resumenY + 10;
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'advance');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'arrearRate');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'amountNet');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'amountNet'); // Monto retenido
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'daysToCount');

    // Calcular la posición Y para la sección de recaudaciones
    const maxResumenY = Math.max(resumenCol1Y, resumenCol2Y);
    const recaudaY = maxResumenY + 10;

    let recaudaCol1Y = recaudaY + 10;
    recaudaCol1Y = this.calculateNextY(recaudaCol1Y, 'documentConciliation');
    recaudaCol1Y = this.calculateNextY(
      recaudaCol1Y,
      'documentAmountDiscounted',
    );

    let recaudaCol2Y = recaudaY + 10;
    recaudaCol2Y = this.calculateNextY(recaudaCol2Y, 'arrearConciliation');

    // Retornar la posición final de las recaudaciones
    const maxRecaudaY = Math.max(recaudaCol1Y, recaudaCol2Y);
    return maxRecaudaY;
  }

  private getSummarySectionEndY(): number {
    const initialY = 35;

    // Primera columna - Información básica
    let col1Y = initialY;
    col1Y = this.calculateNextY(col1Y, 'businessIdentifier');
    col1Y = this.calculateNextY(col1Y, 'stakeholderIdentifier');
    col1Y = this.calculateNextY(col1Y, 'requestId');
    col1Y = this.calculateNextY(col1Y, 'type');

    // Segunda columna - Información básica
    let col2Y = initialY;
    col2Y = this.calculateNextY(col2Y, 'businessName');
    col2Y = this.calculateNextY(col2Y, 'stakeholderName');
    col2Y = this.calculateNextY(col2Y, 'folio');
    col2Y = this.calculateNextY(col2Y, 'status');

    // Calcular la posición Y para la sección de resumen basada en la altura máxima de las columnas
    const maxBasicInfoY = Math.max(col1Y, col2Y);
    const resumenY = maxBasicInfoY + 5;

    // Primera columna - Resumen operación
    let resumenCol1Y = resumenY + 10;
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'operationDate');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'rate');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'amount');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'interestCost');
    resumenCol1Y = this.calculateNextY(resumenCol1Y, 'paymentDate');

    // Segunda columna - Resumen operación
    let resumenCol2Y = resumenY + 10;
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'advance');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'arrearRate');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'amountNet');
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'amountNet'); // Monto retenido
    resumenCol2Y = this.calculateNextY(resumenCol2Y, 'daysToCount');

    // Calcular la posición Y para la sección de recaudaciones
    const maxResumenY = Math.max(resumenCol1Y, resumenCol2Y);
    const recaudaY = maxResumenY + 10;

    let recaudaCol1Y = recaudaY + 10;
    recaudaCol1Y = this.calculateNextY(recaudaCol1Y, 'documentConciliation');
    recaudaCol1Y = this.calculateNextY(
      recaudaCol1Y,
      'documentAmountDiscounted',
    );

    let recaudaCol2Y = recaudaY + 10;
    recaudaCol2Y = this.calculateNextY(recaudaCol2Y, 'arrearConciliation');

    // Retornar la posición final de las recaudaciones
    const maxRecaudaY = Math.max(recaudaCol1Y, recaudaCol2Y);
    return maxRecaudaY;
  }

  private calculateNextY(
    currentY: number,
    fieldName: keyof FieldVisibilityConfig,
  ): number {
    if (this.shouldShowField(fieldName)) {
      return currentY + 5; // Retorna la siguiente posición Y
    }
    return currentY; // Retorna la misma posición Y si no se muestra
  }

  public async generate(): Promise<void> {
    await this.addTitleSection();
    this.addSummarySection();
    this.addContentSection();
    if (this.shouldShowField('rateHistory') && this.rateHistory?.summary) {
      this.addRateHistorySection();
    }
    this.saveDocument();
  }

  private getConciliationTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      payIn: 'Recaudación',
      fund: 'Pago de fondo',
      quote: 'Aplicación en operación',
      credit: 'Aplicación en crédito',
      passive: 'Devolución de excedentes',
      creditNote: 'Nota de crédito',
      discount: 'Descuento',
    };
    return labels[type] || type;
  }

  private getArrearRateForConciliation(log: any): string | null {
    if (
      !this.rateHistory?.conciliations ||
      this.rateHistory.conciliations.length === 0
    ) {
      return null;
    }

    const logDate = new Date(log.date);
    const logDateStr = formatDate(logDate, 'yyyy-mm-dd');
    const logAmount = log.amount || 0;
    const logTypeEsp = log.typeEsp || '';

    // Buscar coincidencia en rateHistory.conciliations
    const match = this.rateHistory.conciliations.find((conciliation) => {
      const conciliationDate = new Date(conciliation.date);
      const conciliationDateStr = formatDate(conciliationDate, 'yyyy-mm-dd');

      // Comparar fechas (solo la parte de fecha, sin hora)
      const datesMatch = logDateStr === conciliationDateStr;

      // Comparar montos con tolerancia de 0.01 para manejar redondeos
      const amountDiff = Math.abs(logAmount - conciliation.amount);
      const amountsMatch = amountDiff < 0.01;

      // Comparar tipos
      const conciliationTypeLabel = this.getConciliationTypeLabel(
        conciliation.type,
      );
      const typesMatch =
        logTypeEsp === conciliationTypeLabel ||
        (conciliation.metadata?.typeEsp &&
          logTypeEsp === conciliation.metadata.typeEsp);

      // También intentar match por metadata si está disponible
      const metadataMatch =
        conciliation.metadata?.requestId &&
        log.requestId &&
        String(conciliation.metadata.requestId) === String(log.requestId);

      return datesMatch && amountsMatch && (typesMatch || metadataMatch);
    });

    return match ? `${match.arrearRate}%` : null;
  }

  private addRateHistorySection(): void {
    if (!this.rateHistory) return;

    // Siempre agregar una nueva página antes del historial de tasas
    this.doc.addPage();
    let startY = 20;

    // Título de la sección
    this.drawText('Detalle de tasas de mora', 10, startY, {
      size: 12,
      bold: true,
    });

    startY += 10;

    // Información general
    const infoY = startY;
    this.drawText(
      `Fecha de vencimiento: ${formatDate(new Date(this.rateHistory.paymentDate), 'dd-mm-yyyy')}`,
      10,
      infoY,
      { size: 9 },
    );
    this.drawText(
      `Tasa actual: ${this.rateHistory.currentArrearRate}%`,
      10,
      infoY + 5,
      { size: 9 },
    );
    this.drawText(
      `Tipo de cálculo: ${this.rateHistory.arrearType === 'table' ? 'Tabla segmentada' : 'Lineal'}`,
      10,
      infoY + 10,
      { size: 9 },
    );

    startY = infoY + 20;

    // Resumen de conciliaciones
    this.drawText('Resumen de Conciliaciones', 10, startY, {
      size: 10,
      bold: true,
    });
    startY += 8;

    const summaryBody = [
      [
        'Total conciliaciones',
        this.rateHistory.summary.totalConciliations.toString(),
      ],
      ['Monto total', formatMoney(this.rateHistory.summary.totalAmount)],
      ['Tasa promedio', `${this.rateHistory.summary.averageRate.toFixed(2)}%`],
    ];

    if (this.rateHistory.summary.dateRange.firstConciliation) {
      summaryBody.push([
        'Primera conciliación',
        formatDate(
          new Date(this.rateHistory.summary.dateRange.firstConciliation),
          'dd-mm-yyyy',
        ),
      ]);
    }

    if (this.rateHistory.summary.dateRange.lastConciliation) {
      summaryBody.push([
        'Última conciliación',
        formatDate(
          new Date(this.rateHistory.summary.dateRange.lastConciliation),
          'dd-mm-yyyy',
        ),
      ]);
    }

    autoTable(this.doc, {
      startY: startY,
      head: [['Concepto', 'Valor']],
      body: summaryBody,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 50 },
      },
    });
  }

  private getEstadoEndY(): number {
    const recaudacionesEndY = this.getRecaudacionesEndY();
    const tableHeight = (this.document.conciliationLog?.length || 0) * 8 + 20;
    const estadoY = recaudacionesEndY + 10 + tableHeight + 10;
    const estadoBodyCount = [
      'arrearAmount',
      'documentRemainingAmountNet',
      'totalRemainingNet',
      'creditNoteAmount',
    ].filter((field) =>
      this.shouldShowField(field as keyof FieldVisibilityConfig),
    ).length;

    const estadoTableHeight =
      estadoBodyCount > 0 ? estadoBodyCount * 8 + 20 : 0;
    return estadoY + 5 + estadoTableHeight;
  }
}
